import { forwardRef, memo, ReactNode, Ref, useImperativeHandle } from 'react';
import classNames from 'classnames';

import { useAnimateElement } from '~/hooks/useAnimateElement';

import { ReactComponent as ErrorIcon } from '~/assets/images/iconsLibrary/alert-triangle.svg';
import { ReactComponent as SuccessIcon } from '~/assets/images/iconsLibrary/check-circle.svg';
import { ReactComponent as WarningIcon } from '~/assets/images/iconsLibrary/info-circle.svg';

import styles from './Snackbar.module.scss';

export enum ESnackbarVariant {
  WARNING = 'warning',
  ERROR = 'error',
  SUCCESS = 'success',
}

export interface ISnackbarProps {
  variant: ESnackbarVariant;
  children?: ReactNode | undefined;
  onClose?: () => void;
}

export interface ISnackbarRef {
  animateOut: (
    beforeAnimation?: () => void | Promise<void>,
  ) => Promise<Animation | null>;
}

const VariantToIconMap = {
  [ESnackbarVariant.WARNING]: WarningIcon,
  [ESnackbarVariant.ERROR]: ErrorIcon,
  [ESnackbarVariant.SUCCESS]: SuccessIcon,
} as const;

function SnackbarComponent(
  { variant = ESnackbarVariant.WARNING, children, onClose }: ISnackbarProps,
  ref: Ref<ISnackbarRef>,
): JSX.Element {
  const { ref: elementRef, animate } = useAnimateElement<HTMLDivElement>({
    options: { duration: 250 },
    mountKeyframes: ({ height, breakpoints }) => ({
      transform: [
        `translateY(${height + (breakpoints.isMobileView ? 20 : 40)}px)`,
        'translateY(0)',
      ],
      opacity: [0.5, 1],
    }),
    animationKeyframes: ({ height, breakpoints }) => ({
      transform: [
        'translateY(0)',
        `translateY(${height + (breakpoints.isMobileView ? 20 : 40)}px)`,
      ],
      opacity: [1, 0.5],
    }),
  });

  useImperativeHandle(
    ref,
    () => ({
      animateOut: () => {
        if (onClose) {
          onClose();
        }
        return animate();
      },
    }),
    [animate, onClose],
  );

  const IconComponent = VariantToIconMap[variant];

  return (
    <div
      ref={elementRef}
      className={classNames(styles.container, styles[variant])}
    >
      <IconComponent className={styles.icon} />
      {children}
    </div>
  );
}

const Snackbar = memo(
  forwardRef<ISnackbarRef, ISnackbarProps>(SnackbarComponent),
);
export default Snackbar;
